import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import StatusBadge from "../components/StatusBadge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Inbox, ChevronRight, Search, X } from "lucide-react";

export default function Queue() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [stageType, setStageType] = useState("all");

  useEffect(() => {
    api.get("/dashboard/queue").then((r) => setItems(r.data));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((i) => {
      if (stageType !== "all" && i.stage?.stage_type !== stageType) return false;
      if (!q) return true;
      return (
        (i.project?.name || "").toLowerCase().includes(q) ||
        (i.id || "").toLowerCase().includes(q) ||
        t(i.stage?.stage_type || "").toLowerCase().includes(q)
      );
    });
  }, [items, query, stageType, t]);

  return (
    <div className="space-y-6">
      <div>
        <div className="label-overline mb-1">{t("queue")}</div>
        <h1 className="font-heading font-black text-4xl tracking-tight">
          Aktiv Sorğular
        </h1>
        <p className="text-slate-600 mt-2">
          {t("waiting_for_action")} · {items.length} ədəd
        </p>
      </div>

      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Layihə, ID və ya mərhələ tipi..."
              className="pl-9 pr-9 bg-white"
              data-testid="queue-search"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                data-testid="queue-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Select value={stageType} onValueChange={setStageType}>
            <SelectTrigger className="w-[200px] bg-white" data-testid="queue-stage-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Bütün mərhələ tipləri</SelectItem>
              <SelectItem value="site_cleaning">{t("site_cleaning")}</SelectItem>
              <SelectItem value="fill_layers">{t("fill_layers")}</SelectItem>
              <SelectItem value="road_base">{t("road_base")}</SelectItem>
              <SelectItem value="asphalt_coating">{t("asphalt_coating")}</SelectItem>
            </SelectContent>
          </Select>
          <span className="mono text-xs text-slate-500" data-testid="queue-count">
            {filtered.length} / {items.length}
          </span>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
          <Inbox className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">
            {items.length === 0 ? t("no_pending") : "Axtarışa uyğun sorğu yoxdur"}
          </p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {filtered.map((i) => (
            <Link
              key={i.id}
              to={`/inspections/${i.id}`}
              className="bg-white border border-slate-200 rounded-lg p-5 hover:-translate-y-0.5 hover:shadow-md hover:border-[#004D99] transition-all"
              data-testid={`queue-item-${i.id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="mono text-xs text-slate-500">
                  #{i.id.slice(0, 8)}
                </div>
                <StatusBadge status={i.status} />
              </div>
              <div className="font-heading font-bold text-lg mb-1 truncate">
                {i.project?.name}
              </div>
              <div className="text-sm text-slate-600">
                {t(i.stage?.stage_type)} ·{" "}
                <span className="mono">
                  {i.stage?.L} × {i.stage?.W}
                  {i.stage?.T ? ` × ${i.stage?.T}` : ""}
                </span>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                <div className="mono text-xs text-slate-400">
                  Step {i.current_step_index + 1}/{i.workflow.length}
                </div>
                <ChevronRight className="w-4 h-4 text-[#004D99]" />
              </div>
              {i.current_assignee && (
                <div
                  className={`mt-2 flex items-center gap-2 text-xs px-2 py-1 rounded ${
                    i.is_mine
                      ? "bg-blue-50 text-blue-800"
                      : "bg-slate-50 text-slate-600"
                  }`}
                  data-testid={`queue-assignee-${i.id}`}
                >
                  <span className="label-overline">Hazırda:</span>
                  <span className="font-semibold truncate">
                    {i.is_mine ? "Sizdə" : i.current_assignee.full_name}
                  </span>
                  <span className="mono text-[10px] text-slate-400 ml-auto">
                    {t(`role_label.${i.current_assignee.role}`)}
                  </span>
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
